import {useCallback} from 'react'
import {type AppBskyActorSearchActors} from '@atproto/api'
import {
  type InfiniteData,
  keepPreviousData,
  type QueryClient,
  type QueryKey,
  useInfiniteQuery,
} from '@tanstack/react-query'

import {IS_CLEAR_COMMUNITY_HANDLE} from '#/lib/constants'
import {STALE} from '#/state/queries'
import {
  type LocalProfile,
  matchesQuery,
  useLocalAccountsQuery,
} from '#/state/queries/community-local-accounts'
import {useAgent} from '#/state/session'

export const RQKEY_ROOT = 'actor-search'
export const RQKEY = (query: string, limit?: number) => [
  RQKEY_ROOT,
  query,
  limit,
]

export function useActorSearch({
  query,
  enabled,
  maintainData,
  limit = 25,
}: {
  query: string
  enabled?: boolean
  maintainData?: boolean
  limit?: number
}) {
  const agent = useAgent()
  const localAccounts = useLocalAccountsQuery()
  return useInfiniteQuery<
    AppBskyActorSearchActors.OutputSchema,
    Error,
    InfiniteData<AppBskyActorSearchActors.OutputSchema>,
    QueryKey,
    string | undefined
  >({
    staleTime: STALE.MINUTES.FIVE,
    queryKey: RQKEY(query, limit),
    queryFn: async ({pageParam}) => {
      const res = await agent.searchActors({
        q: query,
        limit,
        cursor: pageParam,
      })
      return res.data
    },
    enabled: enabled && !!query,
    initialPageParam: undefined,
    getNextPageParam: lastPage => lastPage.cursor,
    placeholderData: maintainData ? keepPreviousData : undefined,
    select: useCallback(
      (data: InfiniteData<AppBskyActorSearchActors.OutputSchema>) => {
        const localMatches = (localAccounts.data ?? []).filter(actor =>
          matchesQuery(actor, query),
        )
        return select(data, localMatches)
      },
      [query, localAccounts.data],
    ),
  })
}

function select(
  data: InfiniteData<AppBskyActorSearchActors.OutputSchema>,
  localMatches: LocalProfile[],
) {
  // enforce uniqueness
  const dids = new Set()

  return {
    ...data,
    pages: data.pages.map((page, i) => ({
      actors: (i === 0 ? [...localMatches, ...page.actors] : page.actors)
        .filter(actor => {
          if (dids.has(actor.did)) {
            return false
          }
          dids.add(actor.did)
          return true
        })
        .sort(
          (a, b) =>
            Number(IS_CLEAR_COMMUNITY_HANDLE(b.handle)) -
            Number(IS_CLEAR_COMMUNITY_HANDLE(a.handle)),
        ),
    })),
  }
}

export function* findAllProfilesInQueryData(
  queryClient: QueryClient,
  did: string,
) {
  const queryDatas = queryClient.getQueriesData<
    InfiniteData<AppBskyActorSearchActors.OutputSchema>
  >({
    queryKey: [RQKEY_ROOT],
  })
  for (const [_queryKey, queryData] of queryDatas) {
    if (!queryData) {
      continue
    }
    for (const actor of queryData.pages.flatMap(page => page.actors)) {
      if (actor.did === did) {
        yield actor
      }
    }
  }
}
